import React, { FC, useMemo, useState, useCallback, useContext, ReactNode } from 'react';

import { ControlInfo } from 'ia-react-core/~/interfaces/ControlInfo';
import { FormState } from 'ia-react-core/~/interfaces/FormState';
import { ErrorMap } from 'ia-react-core/~/interfaces/ErrorMap';
import { MessageType } from 'ia-react-core/~/interfaces/MessageType';
import { ValidatorMap } from 'ia-react-core/~/interfaces/ValidatorMap';
import { ValidatorConfig } from 'ia-react-core/~/interfaces/ValidatorConfig';
import { Validator } from 'ia-react-core/~/interfaces/Validator';
import ControlGroupContext from '../_ControlGroupContext';
import useScopedSelector from '../../../hooks/useScopedSelector';
import FormContext from '../FormContext';
import get from '../../../utils/accessors/get';

export interface ControlGrouperRenderProps {
  controls: ControlInfo[];
  errors: ErrorMap;
  warnings: ErrorMap;
  cautions: ErrorMap;
  validators?: ValidatorMap<any>;
  messages: Record<string, MessageType<any>>;
}

export interface ControlGrouperProps {
  children?: (renderProps: ControlGrouperRenderProps) => ReactNode;
}

/**
 * @mode developer
 */
const ControlGrouper: FC<ControlGrouperProps> = ({ children }) => {
  const [controls, setControls] = useState<ControlInfo[]>([]);

  const registerControlGroup = useCallback((controlInfo: ControlInfo) => {
    setControls((currentControls) => [...currentControls, controlInfo]);
  }, []);

  const unregisterControlGroup = useCallback((controlId: number) => {
    setControls((currentControls) => {
      const index = currentControls.findIndex((controlInfo) => controlInfo.controlId === controlId);
      const nextControls = [...currentControls];

      if (index !== -1) nextControls.splice(index, 1);

      return nextControls;
    });
  }, []);

  const controlGroupContext = useMemo(() => ({
    isControlGroup: true,
    controls,
    registerControlGroup,
    unregisterControlGroup,
  }), [controls, registerControlGroup, unregisterControlGroup]);

  const { ancestorFormReducerKey } = useContext(FormContext);

  const formState = useScopedSelector<FormState>(`${ancestorFormReducerKey}`);

  const getName = useCallback((name: string) => (
    name.replace(/[\]]/g, '').replace(/[[]]/g, '.').split('.')
  ), []);

  const getControlFormState = useCallback((controlInfo: ControlInfo) => {
    if (controlInfo.formScope?.length === 1) return formState;
    const formScope = controlInfo.formScope.slice(1);
    const subFormState = get(formState, `forms.${formScope.join('.forms.')}`);
    return subFormState;
  }, [formState]);

  const getTouched = useCallback((controlInfo: ControlInfo) => {
    const controlFormState = getControlFormState(controlInfo);
    return get(controlFormState, ['touched', ...getName(controlInfo.name)]);
  }, [getControlFormState, getName]);

  const getControlErrors = useCallback((controlInfo: ControlInfo): ErrorMap => {
    const controlFormState = getControlFormState(controlInfo);
    return get(controlFormState, ['errors', ...getName(controlInfo.name)], {}) as ErrorMap;
  }, [getControlFormState, getName]);

  const getControlWarnings = useCallback((controlInfo: ControlInfo): ErrorMap => {
    const controlFormState = getControlFormState(controlInfo);
    return get(controlFormState, ['warnings', ...getName(controlInfo.name)], {}) as ErrorMap;
  }, [getControlFormState, getName]);

  const getControlCautions = useCallback((controlInfo: ControlInfo): ErrorMap => {
    const controlFormState = getControlFormState(controlInfo);
    return get(controlFormState, ['cautions', ...getName(controlInfo.name)], {}) as ErrorMap;
  }, [getControlFormState, getName]);

  const getClientErrors = useCallback((controlInfo: ControlInfo): ErrorMap => {
    const formScope = controlInfo.formScope.slice(1);
    return get(formState.clientErrors.controlErrors, [...formScope, ...getName(controlInfo.name)]);
  }, [formState.clientErrors.controlErrors, getName]);

  const getClientWarnings = useCallback((controlInfo: ControlInfo): ErrorMap => {
    const formScope = controlInfo.formScope.slice(1);
    return get(formState.clientErrors.controlWarnings, [...formScope, ...getName(controlInfo.name)]);
  }, [formState.clientErrors.controlWarnings, getName]);

  const getClientCautions = useCallback((controlInfo: ControlInfo): ErrorMap => {
    const formScope = controlInfo.formScope.slice(1);
    return get(formState.clientErrors.controlCautions, [...formScope, ...getName(controlInfo.name)]);
  }, [formState.clientErrors.controlCautions, getName]);

  const getPrefix = useCallback((controlInfo: ControlInfo) => {
    const formScope = controlInfo.formScope.slice(1);
    if (!formScope.length) return '';
    return `${controlInfo.formScope.slice(1).join('__')}__`;
  }, []);

  const errors = useMemo(() => (
    controls.reduce((result, controlInfo) => {
      const touched = getTouched(controlInfo);
      if (!touched) return result;
      const controlErrors = getControlErrors(controlInfo);
      const clientErrors = getClientErrors(controlInfo);
      const allErrors = { ...controlErrors, ...clientErrors };

      Object.keys(allErrors).forEach((validationType) => {
        result[`${getPrefix(controlInfo)}${controlInfo.name}__${validationType}`] = allErrors[validationType];
      });
      return result;
    }, {} as ErrorMap)
  ), [controls, getTouched, getControlErrors, getClientErrors, getPrefix]);

  const warnings = useMemo(() => (
    controls.reduce((result, controlInfo) => {
      const touched = getTouched(controlInfo);
      if (!touched) return result;
      const controlWarnings = getControlWarnings(controlInfo);
      const clientWarnings = getClientWarnings(controlInfo);
      const allWarnings = { ...controlWarnings, ...clientWarnings };
      Object.keys(allWarnings).forEach((validationType) => {
        result[`${getPrefix(controlInfo)}${controlInfo.name}__${validationType}`] = allWarnings[validationType];
      });
      return result;
    }, {} as ErrorMap)
  ), [controls, getTouched, getControlWarnings, getClientWarnings, getPrefix]);

  const cautions = useMemo(() => (
    controls.reduce((result, controlInfo) => {
      const touched = getTouched(controlInfo);
      if (!touched) return result;
      const controlCautions = getControlCautions(controlInfo);
      const clientCautions = getClientCautions(controlInfo);
      const allCautions = { ...controlCautions, ...clientCautions };
      Object.keys(allCautions).forEach((validationType) => {
        result[`${getPrefix(controlInfo)}${controlInfo.name}__${validationType}`] = allCautions[validationType];
      });
      return result;
    }, {} as ErrorMap)
  ), [controls, getTouched, getControlCautions, getClientCautions, getPrefix]);

  const messages = useMemo(() => (
    controls.reduce((result, controlInfo) => {
      if (controlInfo.validationMessages) {
        Object.keys(controlInfo.validationMessages).forEach((validationType) => {
          result[`${getPrefix(controlInfo)}${controlInfo.name}__${validationType}`] = controlInfo.validationMessages[validationType];
        });
      }
      if (controlInfo.validators) {
        Object.keys(controlInfo.validators).forEach((validationType) => {
          if (controlInfo.validators[validationType]?.message) {
            result[`${getPrefix(controlInfo)}${controlInfo.name}__${validationType}`] = controlInfo.validators[validationType]?.message;
          }
        });
      }
      return result;
    }, {} as Record<string, MessageType<any>>)
  ), [controls, getPrefix]);

  const validators = useMemo(() => (
    controls.reduce((result, controlInfo) => {
      if (controlInfo.validators) {
        Object.keys(controlInfo.validators).forEach((validationType) => {
          result[`${getPrefix(controlInfo)}${controlInfo.name}__${validationType}`] = controlInfo.validators[validationType];
        });
      }
      return result;
    }, {} as Record<string, Validator<any> | ValidatorConfig<any>>)
  ), [controls, getPrefix]);

  return (
    <ControlGroupContext.Provider value={controlGroupContext}>
      {children({ controls, errors, warnings, cautions, messages, validators })}
    </ControlGroupContext.Provider>
  );
};
ControlGrouper.displayName = 'ControlGroup';

export default ControlGrouper;
